import ApiService from '@/services/apiService';
import { kebabCaseToCamelCase } from '@/utils/vuex';
import tenantPickerMixin from '@/components/core/tenant/pickerMixin';
import { themeDefaults, themeColors } from '@/utils/themes';
import { templateVariables } from '@/utils/emailTemplateVariables';
import routerPaths from '@/router/paths';
import { isEmpty, get, set, cloneDeep, mergeWith } from 'lodash';
import { EventBus } from '@/services/eventbus.js';
const mergeWithReplaceArrays = (objValue, srcValue) => {
  if (Array.isArray(objValue)) {
    return srcValue;
  }
};
const emailTemplatesOrder = [{
  template: 'is-fm-access-created',
  permissions: {
    subject: 'IsFmTicket',
    action: 'create'
  }
}, {
  template: 'is-fm-access-revoked',
  permissions: {
    subject: 'IsFmTicket',
    action: 'create'
  }
}, {
  template: 'password-changed'
}, {
  template: 'password-reset'
}, {
  template: 'ticket-created',
  permissions: {
    subject: 'IsFmTicket',
    action: 'create'
  }
}, {
  template: 'ticket-created-manager',
  permissions: {
    subject: 'IsFmTicket',
    action: 'create'
  }
}, {
  template: 'user-create-password-reset'
}, {
  template: 'user-locked'
}, {
  template: 'tenant-bridge-connectionerror'
}];
export default {
  name: 'ViewAccountMgmtAccountMgmtSettings',
  mixins: [tenantPickerMixin],
  data() {
    return {
      emailTemplates: {},
      formIsValid: false,
      isSaving: false,
      imageUploads: {},
      colors: themeColors,
      themeDefaults: {
        ...themeDefaults,
        domains: [],
        footerDE: {
          imprintUrl: '',
          aboutUsUrl: '',
          copyrightText: '',
          copyrightUrl: ''
        },
        footerEN: {
          imprintUrl: '',
          aboutUsUrl: '',
          copyrightText: '',
          copyrightUrl: ''
        }
      },
      tenantTheme: {},
      tenantEmail: {
        defaults: {}
      },
      termsAndConditions: {},
      termsAndConditionsVisible: false,
      requestConsentTermsAndConditions: true,
      togglePositionTC: false,
      customEmailsVisible: false,
      useCustomEmail: false,
      emailTplVariables: templateVariables,
      feedback: {},
      scopeId: 0,
      imageCacheBuster: 0
    };
  },
  computed: {
    headerBG() {
      return './img/dtwin-factory1.png';
    },
    drawerIconLinks() {
      return routerPaths.filter(item => (item.meta || {}).isDrawerIconLink).map(item => ({
        ...item,
        name: this.$i18n_t(item.meta.i18n)
      }));
    },
    hasDomains() {
      return this.tenantTheme.domains.length > 0;
    },
    emailRules() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired'), v => /.+@.+\..+/.test(v) || this.$i18n_t('Common.emailValid')];
    },
    togglePositionTCC() {
      return this.togglePositionTC;
    },
    bgPositionItems() {
      return [{
        value: 'left',
        text: this.$i18n_t('accMgmt.system.theme.positions.left')
      }, {
        value: 'right',
        text: this.$i18n_t('accMgmt.system.theme.positions.right')
      }, {
        value: 'center',
        text: this.$i18n_t('accMgmt.system.theme.positions.center')
      }, {
        value: 'center-overlap',
        text: this.$i18n_t('accMgmt.system.theme.positions.center-overlap')
      }];
    }
  },
  watch: {
    async selectedTenantId() {
      if (this.selectedTenantId === undefined) {
        return;
      }
      const tenant = this.getTenantById(this.selectedTenantId);
      this.tenantTheme = tenant.data && tenant.data.theme || {};
      let themeToUse = this.tenantTheme;
      if (!this.tenantTheme || isEmpty(this.tenantTheme)) {
        const rootTenant = this.tenants.find(t => !t.parentId);
        themeToUse = get(rootTenant, 'data.theme', {});
      }
      this.tenantTheme = mergeWith(cloneDeep(this.themeDefaults), themeToUse, mergeWithReplaceArrays);
      this.imageCacheBuster = Date.now();
      if (tenant.data.email) {
        this.tenantEmail = tenant.data.email;
      }
      this.termsAndConditions = tenant.properties && tenant.properties.termsAndConditions ? tenant.properties.termsAndConditions : {
        title: {
          de: '',
          en: ''
        }
      };
      this.togglePositionTC = this.termsAndConditions && this.termsAndConditions.de && this.termsAndConditions.en ? true : false;
      this.useCustomEmail = get(tenant, 'data.feedback') ? true : false;
      this.requestConsentTermsAndConditions = true;
      this.termsAndConditionsVisible = this.togglePositionTC;
      this.customEmailsVisible = this.useCustomEmail;
      this.tenantEmail.siteUrl = this.getDomainSelected();
      this.loadEmailTemplates(this.selectedTenantId);
      this.scopeId = tenant.scopeId;
      this.feedback = get(tenant, 'data.feedback') || {};
    }
  },
  mounted() {
    this.loadTenants();
  },
  methods: {
    fullImageUrl(item) {
      // Ensure upload-based theme assets are routed via public theming endpoints (no auth required)
      if (!item) {
        return item;
      }
      const tenantId = this.selectedTenantId;
      const domain = this.getDomainSelected();
      const qpTenant = tenantId ? `tenantId=${tenantId}` : '';
      const qpDomain = domain ? `domain=${encodeURIComponent(domain)}` : '';
      const qp = [qpTenant, qpDomain].filter(Boolean).join('&');

      // For upload URLs, map to public theming endpoints and add a cache-buster to avoid stale images post-save
      if (item.includes('/upload/')) {
        const b = this.imageCacheBuster ? `&_=${this.imageCacheBuster}` : '';
        if (item === this.tenantTheme.favicon) {
          return `${location.origin}/api/v1/theming/favicon?${qp}${b}`;
        }
        if (item === this.tenantTheme.lightLogo) {
          return `${location.origin}/api/v1/theming/logo?mode=light&${qp}${b}`;
        }
        if (item === this.tenantTheme.darkLogo) {
          return `${location.origin}/api/v1/theming/logo?mode=dark&${qp}${b}`;
        }
        if (item === this.tenantTheme.lightBackground) {
          return `${location.origin}/api/v1/theming/background?mode=light&${qp}${b}`;
        }
        if (item === this.tenantTheme.darkBackground) {
          return `${location.origin}/api/v1/theming/background?mode=dark&${qp}${b}`;
        }
        // Fallback: absolute upload URL when authenticated
        if (item.startsWith('/')) {
          return `${location.origin}${item}`;
        }
        return item;
      }

      // Absolute external URL: keep as-is
      if (/^http(s?):\/\//.test(item)) {
        return item;
      }

      // API-relative URL: prefix with API host
      if (item.startsWith('/api/')) {
        return `${location.origin}${item}`;
      }
      return item;
    },
    isDefaultLogo(item) {
      return item === themeDefaults.lightLogo || item === themeDefaults.darkLogo;
    },
    getDomains() {
      return this.tenantTheme.domains.length === 0 ? [this.tenantEmail.defaults.siteUrl] : this.tenantTheme.domains;
    },
    getDomainSelected() {
      if (this.tenantEmail.siteUrl && this.tenantTheme.domains.includes(this.tenantEmail.siteUrl)) {
        return this.tenantEmail.siteUrl;
      }
      return this.tenantTheme.domains.length === 0 ? this.tenantEmail.defaults.siteUrl : this.tenantTheme.domains[0];
    },
    revalidateForm() {
      this.$refs.mainForm.validate();
    },
    convertTemplateKey(key) {
      return kebabCaseToCamelCase(key);
    },
    checkPermissions(permissionSet, userPermissions) {
      // {
      //   or: [
      //     {and: [
      //       { subject: 'DigitalTwin', action: 'manage' },
      //       { subject: 'AutoID', action: 'read', inverted: true }
      //     ]},
      //     { subject: 'all', action: 'manage' }
      //   ]
      // }

      if (permissionSet.or && Array.isArray(permissionSet.or)) {
        return permissionSet.or.find(permission => this.checkPermissions(permission, userPermissions));
      } else if (permissionSet.and && Array.isArray(permissionSet.and)) {
        return permissionSet.and.every(permission => this.checkPermissions(permission, userPermissions));
      }
      const hasPermission = userPermissions[`${permissionSet.subject}.${permissionSet.action}`];
      return hasPermission === !permissionSet.inverted;
    },
    loadEmailTemplates(tenantId) {
      ApiService.getAllEmailTemplates(tenantId).then(data => {
        const allowedTemplates = emailTemplatesOrder.filter(item => {
          if (item.permissions) {
            return this.checkPermissions(item.permissions, this.permissions);
          }
          return true;
        });
        const templates = allowedTemplates.reduce((acc, cur) => {
          return {
            ...acc,
            [cur.template]: {
              subject: data[cur.template].subject.de,
              html: data[cur.template].html.de
            }
          };
        }, []);
        this.emailTemplates = templates;
      }).catch(error => {
        console.error(error);
      });
    },
    updateLightBackground(file) {
      this.imageUploads['lightBackground'] = file;
    },
    updateDarkBackground(file) {
      this.imageUploads['darkBackground'] = file;
    },
    updateLightLogo(file, orientation) {
      this.tenantTheme.lightLogoOrientation = orientation;
      this.imageUploads['lightLogo'] = file;
    },
    updateDarkLogo(file, orientation) {
      this.tenantTheme.darkLogoOrientation = orientation;
      this.imageUploads['darkLogo'] = file;
    },
    updateFavicon(file, orientation) {
      const validImageFileName = /\.png$/i.exec(file.name);
      if (!validImageFileName) {
        this.$refs['favicon'].resetThumbnail();
        this.$snack(this.$i18n_t('accMgmt.system.theme.pngImagesAllowed'), 'error');
        return;
      }
      if (orientation !== 'square') {
        this.$refs['favicon'].resetThumbnail();
        this.$snack(this.$i18n_t('accMgmt.system.theme.squareImagesAllowed'), 'error');
        return;
      }
      this.imageUploads['favicon'] = file;
    },
    setPrimaryColorLight(color) {
      this.tenantTheme.lightHighlight = color === 'customerColor' ? 'customerColorLight' : color;
      if (color !== 'customerColor') {
        delete this.tenantTheme.lightHighlightCustomer;
      }
    },
    setPrimaryColorLightCustomer(hex) {
      this.tenantTheme.lightHighlight = 'customerColorLight';
      this.tenantTheme.lightHighlightCustomer = hex;
    },
    setPrimaryColorDark(color) {
      this.tenantTheme.darkHighlight = color === 'customerColor' ? 'customerColorDark' : color;
      if (color !== 'customerColor') {
        delete this.tenantTheme.darkHighlightCustomer;
      }
    },
    setPrimaryColorDarkCustomer(hex) {
      this.tenantTheme.darkHighlight = 'customerColorDark';
      this.tenantTheme.darkHighlightCustomer = hex;
    },
    updateTemplate(key, part, content) {
      if (this.emailTemplates[key] && content !== '') {
        if (part === 'subject') {
          this.emailTemplates[key].subject = content;
        } else if (part === 'body') {
          this.emailTemplates[key].html = content;
        }
      }
    },
    saveEmailTemplate(template, templateKey, tenantId) {
      return ApiService.setEmailTemplate(tenantId, templateKey, template).then(data => {
        this.emailTemplates[data.identifier] = {
          subject: data.template.subject.de,
          html: data.template.html.de
        };
      });
    },
    resetTheme() {
      // reset potentially updated thumbnail on the theme image components src
      // check for refs existence because the components for the dark variations
      // are not rendered until their v-tab was activated once
      this.$refs['lightBackground'] && this.$refs['lightBackground'].resetThumbnail();
      this.$refs['darkBackground'] && this.$refs['darkBackground'].resetThumbnail();
      this.$refs['lightLogo'] && this.$refs['lightLogo'].resetThumbnail();
      this.$refs['darkLogo'] && this.$refs['darkLogo'].resetThumbnail();
      this.$refs['favicon'] && this.$refs['favicon'].resetThumbnail();

      // clear potentially assigned theme image uploads
      this.imageUploads = [];

      // reset theme settings to default
      this.tenantTheme = Object.assign(this.tenantTheme, {
        darkBackground: themeDefaults.darkBackground,
        darkHighlight: themeDefaults.darkHighlight,
        darkLogo: themeDefaults.darkLogo,
        darkLogoByline: themeDefaults.darkLogoByline,
        darkLogoOrientation: themeDefaults.darkLogoOrientation,
        darkLogoPosition: themeDefaults.darkLogoPosition,
        lightBackground: themeDefaults.lightBackground,
        lightHighlight: themeDefaults.lightHighlight,
        lightLogo: themeDefaults.lightLogo,
        lightLogoByline: themeDefaults.lightLogoByline,
        lightLogoOrientation: themeDefaults.lightLogoOrientation,
        lightLogoPosition: themeDefaults.lightLogoPosition,
        favicon: themeDefaults.favicon
      });
    },
    saveTheme() {
      const emailTemplatePromises = Object.keys(this.emailTemplates).map(tplKey => {
        const template = {
          subject: {
            de: this.emailTemplates[tplKey].subject
          },
          html: {
            de: this.emailTemplates[tplKey].html
          }
        };
        return this.saveEmailTemplate(template, tplKey, this.selectedTenantId);
      });
      this.isSaving = true;
      const uploadPromises = Object.keys(this.imageUploads).map(key => {
        return this.uploadImage(this.imageUploads[key]).then(fileId => ({
          key,
          url: `/api/v1/upload/${fileId}`
        }));
      });
      Promise.all(emailTemplatePromises).catch(error => {
        console.error(error);
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('accMgmt.system.errorSave'), 'error');
        }
      });
      Promise.all(uploadPromises).then(result => {
        result.forEach(item => {
          this.tenantTheme[item.key] = item.url;
        });
      }).then(() => {
        this.imageUploads = [];
        // Actually update the tenant
        return ApiService.tenantGetById(this.selectedTenantId, ['no-scope-descendents']);
      }).then(result => {
        const tenantObj = result;
        if (!tenantObj.data) {
          tenantObj.data = {};
        }
        tenantObj.data.theme = this.tenantTheme;
        tenantObj.data.email = this.tenantEmail;
        if (this.useCustomEmail) {
          set(tenantObj, 'data.feedback', this.feedback);
        } else {
          set(tenantObj, 'data.feedback', null);
        }
        if (!tenantObj.properties) {
          tenantObj.properties = {};
        }
        tenantObj.properties.termsAndConditions = this.termsAndConditionsVisible ? this.termsAndConditions : null;
        tenantObj.requestConsentTermsAndConditions = this.termsAndConditionsVisible && this.requestConsentTermsAndConditions ? true : false;
        return ApiService.tenantUpdate(this, result);
      }).then(() => {
        // store saved theme data to tenant state
        const tenantIdx = this.getTenantIdxById(this.selectedTenantId);
        if (!this.tenants[tenantIdx].properties) {
          this.tenants[tenantIdx].properties = {};
        }
        this.tenants[tenantIdx].data.theme = this.tenantTheme;
        this.tenants[tenantIdx].data.email = this.tenantEmail;
        this.tenants[tenantIdx].properties.termsAndConditions = this.termsAndConditions;
        this.isSaving = false;
        this.$snack(this.$i18n_t('accMgmt.system.saveSucess'), 'success');
        this.imageCacheBuster = Date.now();
        // Trigger a global branding refresh so new colors/logos apply immediately
        EventBus.$emit('refresh-branding');
      }).catch(error => {
        console.error(error);
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('accMgmt.system.errorSave'), 'error');
        }
        this.isSaving = false;
      });
    },
    uploadImage(file) {
      const formData = new FormData();
      formData.append('file', file);
      return ApiService.saveFile(this, formData).then(result => result.fileId);
    },
    reverseVisibility() {
      this.termsAndConditionsVisible = !this.termsAndConditionsVisible;
    },
    reverseVisibilityCustomEmails() {
      this.customEmailsVisible = !this.customEmailsVisible;
    }
  }
};